/* eslint-disable no-await-in-loop */
import moment from 'moment';
import hash from 'object-hash';
import {
  getNoticeType,
  maybeGetXMLSyncExportSettings,
  removeUndefinedFields
} from '../../helpers';
import { SnapshotModel, getModelFromRef, getModelFromSnapshot } from '..';
import { Collections } from '../../constants';
import {
  ConfirmationStatus,
  NoticeStatusType,
  NoticeType,
  OrganizationStatus
} from '../../enums';
import {
  ENotice,
  ENoticeDraft,
  ERef,
  ESnapshotExists,
  ETemplate,
  ETransaction,
  EUser,
  XMLSyncExportSettings
} from '../../types';
import { TransferStatus } from '../../types/transfers';
import { BadRequestError, NotFoundError } from '../../errors/ColumnErrors';
import { getOrThrow } from '../../utils/refs';
import * as affidavitHelpers from '../../affidavits';
import { NoticePreconditionStatuses } from '../../affidavits/types';
import { confirmNotice } from '../../notice/confirmation';
import { ResponseOrError, wrapError, wrapSuccess } from '../../types/responses';
import { NoticeServiceAgent } from '../../services/NoticeService';
import { SyncData } from '../../types/integrations/sync';
import { getRunModelsFromQuery } from '../../services/runService';
import { createDesignNotesUpdatedEvent } from '../../utils/events';
import { AffidavitTemplateModel } from './affidavitTemplateModel';
import {
  getAffidavitSettingsForNotice,
  getAffidavitSettingsForNoticeData
} from '../../pricing/affidavits';
import { getCustomerForNotice } from '../../notice/customer';
import { noticeIsSubmitted } from '../../notice/helpers';
import { AffidavitReconciliationSettings } from '../../types/organization';
import { getErrorReporter } from '../../utils/errors';
import { safeAsync, safeGetOrThrow } from '../../safeWrappers';
import { buildNoticeAddOnQuote } from '../../pricing/noticeAddOns/quote';
import { getNoticeAAC } from '../../types/affidavits/convertARS';
import { CustomerModel } from './customerModel';
import { OrganizationModel } from './organizationModel';
import { SerializedModel } from '../types';
import { ColumnService } from '../../services/directory';
import {
  IndesignServerClient,
  requestDisplayParameters
} from '../../indesign/request';
import { safeGetModelFromRef } from '../getModel';
import { InvoiceModel } from './invoiceModel';
import { PublicNoticeInvoice } from '../../types/invoices';
import { UserModel } from './userModel';

export type SerializedUserNoticeModel = SerializedModel<
  typeof Collections.userNotices
>;

export class UserNoticeModel extends SnapshotModel<
  ENotice,
  typeof Collections.userNotices
> {
  type = Collections.userNotices;

  // TODO: replace with model once one exists?
  private publisher: OrganizationModel | null = null;

  private affidavitNotarizationPreconditionStatuses: NoticePreconditionStatuses | null =
    null;

  public async getPublisher() {
    if (!this.publisher) {
      this.publisher = await getModelFromRef(
        OrganizationModel,
        this.ctx,
        this.modelData.newspaper
      );
    }
    return this.publisher;
  }

  /**
   * Checks whether the notice is being created for the first time or not based on whether
   * it has some properties.
   *
   * @return {boolean} Returns true if the notice is new, otherwise false.
   */
  public isNew(): boolean {
    return !this.modelData.noticeStatus;
  }

  public isDisplay() {
    return this.modelData.noticeType === NoticeType.display_ad.value;
  }

  /**
   * Lock against parallel transfer processing: transactionally moves transferStatus
   * from PENDING to IN_PROGRESS, failing if it is anything other than PENDING.
   */
  public async setTransferToInProgress(): Promise<ResponseOrError<void>> {
    const [error] = await safeAsync(() =>
      this.ctx.runTransaction(async (tx: ETransaction) => {
        const notice = await tx.get(this.ref);
        const noticeData = notice.data();
        if (!noticeData) {
          throw new NotFoundError('Notice not found');
        }

        if (noticeData.transferStatus !== TransferStatus.PENDING) {
          throw new BadRequestError(
            `Notice transfer status is not PENDING: ${noticeData.transferStatus}`
          );
        }
        tx.update(this.ref, { transferStatus: TransferStatus.IN_PROGRESS });
      })
    )();
    if (error) {
      getErrorReporter().logAndCaptureError(
        ColumnService.FINANCIAL_RECONCILIATION,
        error,
        'Unexpected error during transaction setting notice transfer status to IN_PROGRESS',
        { noticeId: this.id }
      );
      return wrapError(error);
    }
    const [refreshError] = await safeAsync(() => this.refreshData())();
    if (refreshError) {
      // The lock committed, so a failed local refresh must not read as a failed lock:
      // the notice would strand at IN_PROGRESS, which the cron never picks up.
      this.modelData.transferStatus = TransferStatus.IN_PROGRESS;
      getErrorReporter().logAndCaptureError(
        ColumnService.FINANCIAL_RECONCILIATION,
        refreshError,
        'Failed to refresh notice after committing IN_PROGRESS lock; continuing with committed status',
        { noticeId: this.id }
      );
    }
    return wrapSuccess(undefined);
  }

  public containsImages(): boolean {
    return (this.modelData.displayParams?.images?.length ?? 0) > 0;
  }

  private async getPublisherManagedAffidavitTemplate(): Promise<
    AffidavitTemplateModel | undefined
  > {
    const fromNotice = await AffidavitTemplateModel.fromSourceRef(
      this.ctx,
      this,
      moment().format('MM/DD/YYYY'),
      this.modelData.newspaper
    );
    if (fromNotice) return fromNotice;

    const user = await getOrThrow(this.modelData.filer);
    const fromUser = await AffidavitTemplateModel.fromSourceRef(
      this.ctx,
      user,
      user.data().name,
      this.modelData.newspaper
    );
    if (fromUser) return fromUser;

    const rate = await getOrThrow(this.modelData.rate);
    const fromRate = await AffidavitTemplateModel.fromSourceRef(
      this.ctx,
      rate,
      rate.data().description,
      this.modelData.newspaper
    );
    if (fromRate) return fromRate;

    const publisher = await getOrThrow(this.modelData.newspaper);

    const noticeType = getNoticeType(this, publisher, {
      skipDisplayType: true
    });

    const fromNoticeType = await AffidavitTemplateModel.fromNoticeType(
      this.ctx,
      noticeType,
      publisher
    );
    if (fromNoticeType) return fromNoticeType;

    return await AffidavitTemplateModel.fromSourceRef(
      this.ctx,
      publisher,
      publisher.data().name,
      publisher.ref
    );
  }

  private async getColumnManagedAffidavitTemplate(): Promise<
    AffidavitTemplateModel | undefined
  > {
    const settings = await getAffidavitSettingsForNotice(this.ctx, this);

    if (!settings) {
      return;
    }

    if (!settings.affidavitsManagedByColumn) {
      throw new Error('Affidavit settings are not managed by column');
    }

    const fromNotice = await AffidavitTemplateModel.fromSourceRefNotarized(
      this.ctx,
      this,
      moment().format('MM/DD/YYYY'),
      this.modelData.newspaper
    );
    if (fromNotice) return fromNotice;

    const customerSnap = await getCustomerForNotice(this.ctx, this);
    if (customerSnap) {
      const customer = getModelFromSnapshot(
        CustomerModel,
        this.ctx,
        customerSnap
      );
      const customerName = `${customerSnap.data().firstName} ${
        customerSnap.data().lastName
      }`;
      const fromCustomer = await AffidavitTemplateModel.fromSourceRefNotarized(
        this.ctx,
        customer,
        customerName,
        this.modelData.newspaper
      );
      if (fromCustomer) return fromCustomer;
    }

    const publisherSnap = await getOrThrow(this.modelData.newspaper);
    const publisher = getModelFromSnapshot(
      OrganizationModel,
      this.ctx,
      publisherSnap
    );
    const noticeType = getNoticeType(this, publisher);
    const fromNoticeType = await AffidavitTemplateModel.fromNoticeTypeNotarized(
      this.ctx,
      noticeType,
      publisher
    );
    if (fromNoticeType) return fromNoticeType;

    return await AffidavitTemplateModel.fromSourceRefNotarized(
      this.ctx,
      publisher,
      publisher.data().name,
      publisher.ref
    );
  }

  public async getAffidavitTemplate(options: {
    isColumnManaged: boolean;
  }): Promise<AffidavitTemplateModel | undefined> {
    return options.isColumnManaged
      ? this.getColumnManagedAffidavitTemplate()
      : this.getPublisherManagedAffidavitTemplate();
  }

  async createDraft({ asUser }: { asUser: ERef<EUser> | null }) {
    const originalData = { ...this.modelData };
    delete originalData.drafts;

    const draftFields: Partial<ENoticeDraft> = {
      ...originalData,
      original: this.ref,
      owner: asUser
    };

    delete draftFields.editedAt;
    delete draftFields.lastEditedBy;
    delete draftFields.proofStoragePath;

    const newDraftRef = this.ctx.userDraftsRef().doc();
    await newDraftRef.set(draftFields);

    const drafts = this.modelData.drafts || [];
    drafts.push(newDraftRef);
    await this.update({ drafts });

    return newDraftRef;
  }

  /**
   * Updates a notice with draft data.
   *
   * @param {Object} options - The options for updating the notice.
   * @param {ERef<ENoticeDraft>} options.draftRef - The reference to the draft notice.
   * @param {NoticeServiceAgent} options.agent - The agent responsible for the update.
   *
   * @returns {Promise<ResponseOrError<ENotice>>} - A promise that resolves to the updated notice or an error.
   */
  async updateWithDraftData({
    draftRef,
    agent
  }: {
    draftRef: ERef<ENoticeDraft>;
    agent: NoticeServiceAgent;
  }): Promise<ResponseOrError<UserNoticeModel>> {
    const draftSnap = await getOrThrow(draftRef);
    const draft = draftSnap.data();
    if (draft.inactive) {
      return wrapError(new Error('Draft is inactive'));
    }
    await this.addDraftFilesToNotice(draftRef);
    await this.addDraftMailToNotice(draftRef);

    const submissionTime = this.ctx.timestamp();
    const newspaper = await getOrThrow(draftSnap.data().newspaper);
    const cleanedDraft = await this.cleanDraftNoticeProperties(draft);
    if (!cleanedDraft.postWithoutFormatting && !cleanedDraft.pricing) {
      const err = new Error('Draft has null pricing at submission time');
      getErrorReporter().logAndCaptureError(
        ColumnService.PAYMENTS,
        err,
        'updateWithDraftData: null pricing on draft',
        {
          draftId: draftRef.id,
          noticeId: this.ref.id
        }
      );
      // Don't fail for now
    }
    // Freeze what the filer was quoted for add-ons, server-side, at the moment
    // they submit. Runs for every placement path -- web, API, and edits -- so a
    // quote is always present and is never taken from the client.
    const [quoteError, paperLevelSettings] = await safeAsync(() =>
      getAffidavitSettingsForNoticeData(this.ctx, cleanedDraft, {
        skipNoticeLevelSettings: true
      })
    )();
    if (quoteError) {
      getErrorReporter().logAndCaptureError(
        ColumnService.PAYMENTS,
        quoteError,
        'updateWithDraftData: could not resolve settings to quote add-ons',
        { draftId: draftRef.id, noticeId: this.ref.id }
      );
    }
    const noticeAddOnQuote = quoteError
      ? this.modelData.noticeAddOnQuote
      : buildNoticeAddOnQuote(
          paperLevelSettings,
          newspaper.data().noticeAddOnPricing
        );

    const noticeObject: ENoticeDraft = {
      ...cleanedDraft,
      ...(noticeAddOnQuote ? { noticeAddOnQuote } : {}),
      ...(newspaper.data().organizationStatus ===
      OrganizationStatus.in_implementation.value
        ? { testNotice: true }
        : {}),
      noticeStatus: [
        NoticeStatusType.affidavit_approved.value,
        NoticeStatusType.affidavit_submitted.value
      ].includes(this.modelData.noticeStatus)
        ? this.modelData.noticeStatus
        : NoticeStatusType.pending.value,
      confirmedBy: agent.isPublisher
        ? agent.user
        : this.modelData.confirmedBy || null,
      confirmedAt: this.modelData.confirmedAt || submissionTime,
      confirmed: true,
      confirmedReceiptTime: agent.isPublisher ? submissionTime : null,
      ...(this.isNew() ? {} : { editedAt: submissionTime }),
      ...(this.isNew() ? {} : { lastEditedBy: agent.user })
    };
    this.updateWithObjectData(noticeObject);
    // if design notes were edited, create a corresponding event
    if (draft.designNotes) {
      await createDesignNotesUpdatedEvent(
        this.ctx,
        this.ref,
        draft.designNotes
      );
    }
    return wrapSuccess(this);
  }

  public updateWithObjectData(noticeObject: ENotice) {
    Object.assign(this.modelData, noticeObject);
  }

  public async publish() {
    try {
      await this.update(this.modelData);
      return wrapSuccess(null);
    } catch (e) {
      return wrapError(e as Error);
    }
  }

  /**
   * Updates the account number on the notice if the publisher has exportSettings
   *
   * @param {string} accountNumber - The new account number to update.
   * @return {Promise<boolean>} A Promise that resolves to a boolean value indicating whether the notice was updated or not.
   */
  public async maybeUpdateAccountNumberOnNotice(
    accountNumber: string
  ): Promise<boolean> {
    const newspaper = await this.getPublisher();
    const exportSettings: XMLSyncExportSettings | undefined =
      await maybeGetXMLSyncExportSettings(newspaper);
    if (exportSettings) {
      const existingSyncData = this.modelData.syncData || { format: null };
      const updatedSyncData: SyncData = {
        ...existingSyncData,
        ...(exportSettings.format ? { format: exportSettings.format } : {}),
        syncCustomerId: accountNumber
      } as SyncData;
      await this.update({ syncData: updatedSyncData });
      return true;
    }
    return false;
  }

  /**
   * The notice's invoice as a model, or null when the notice has no invoice
   * (never invoiced, or the invoice was cancelled and detached).
   */
  public async getInvoice(): Promise<
    ResponseOrError<InvoiceModel<PublicNoticeInvoice> | null>
  > {
    const invoiceRef = this.modelData.invoice;
    if (!invoiceRef) {
      return wrapSuccess(null);
    }
    const { response: invoice, error } = await safeGetModelFromRef(
      InvoiceModel,
      this.ctx,
      invoiceRef as ERef<PublicNoticeInvoice>
    );
    if (error) {
      return wrapError(error);
    }
    return wrapSuccess(invoice as InvoiceModel<PublicNoticeInvoice>);
  }

  public async getFiler(): Promise<ResponseOrError<UserModel>> {
    return safeGetModelFromRef(UserModel, this.ctx, this.modelData.filer);
  }

  /**
   * Replaces the notice's integration sync data. Integrations own the shape of
   * this field, so they hand over a complete object rather than a patch.
   */
  public async updateSyncData(syncData: SyncData): Promise<void> {
    await this.update({ syncData });
  }

  /**
   * True once a notice is cancelled or has been marked as cancelled by a user.
   * Integrations treat both as cancelled: `cancelledBy` is set before the
   * status transition completes.
   */
  public get isCancelledOrCancelling(): boolean {
    return this.isCancelled || !!this.modelData.cancelledBy;
  }

  public async getAffidavitNotarizationPreconditionStatuses() {
    if (!this.affidavitNotarizationPreconditionStatuses) {
      this.affidavitNotarizationPreconditionStatuses =
        await affidavitHelpers.getNoticePreconditionStatuses({
          ctx: this.ctx,
          notice: this
        });
    }
    return this.affidavitNotarizationPreconditionStatuses;
  }

  public async areAffidavitsEnabled() {
    const newspaperSnap = await this.getPublisher();
    return !affidavitHelpers.isAffidavitDisabled(this.modelData, newspaperSnap);
  }

  public async getRuns(
    options: {
      includeDisabled: boolean;
      includeCancelled: boolean;
      sortOrder: 'asc' | 'desc';
    } = {
      includeDisabled: false,
      includeCancelled: true,
      sortOrder: 'asc'
    }
  ) {
    const runQuery = this.ctx
      .runsRef()
      .where('notice', '==', this.ref)
      .orderBy('publicationDate', options.sortOrder);
    return await getRunModelsFromQuery(this.ctx, runQuery, options);
  }

  public get isConfirmed() {
    return (
      Boolean(this.modelData.confirmedReceipt) ||
      this.modelData.confirmationStatus === ConfirmationStatus.Confirmed
    );
  }

  public get isArchived() {
    return this.modelData.isArchived === true;
  }

  public get isCancelled() {
    return this.modelData.noticeStatus === NoticeStatusType.cancelled.value;
  }

  public get isPending() {
    return this.modelData.noticeStatus === NoticeStatusType.pending.value;
  }

  public get isSubmitted() {
    return noticeIsSubmitted(this);
  }

  public get confirmationStatus() {
    return (
      this.modelData.confirmationStatus ??
      (this.isConfirmed
        ? ConfirmationStatus.Confirmed
        : ConfirmationStatus.Pending)
    );
  }

  public async confirm(confirmedBy: ESnapshotExists<EUser>) {
    await confirmNotice(this.ctx, {
      notice: this,
      user: confirmedBy
    });
  }

  public async markReviewing() {
    await this.update({
      confirmationStatus: ConfirmationStatus.AwaitingConfirmation
    });
  }

  /**
   * Adds the draft files from a notice draft to a notice by copying them
   * and deleting them from the draft.
   *
   * @param {ERef<ENoticeDraft>} draftRef - The reference to the draft notice.
   *
   * @return {Promise<void>} - A promise that resolves when the draft files have been added to the notice.
   */
  public async addDraftFilesToNotice(
    draftRef: ERef<ENoticeDraft>
  ): Promise<void> {
    const draftFilesCollectionRef = this.ctx.userNoticeFilesRef(draftRef);
    const draftFilesCollectionSnap = await draftFilesCollectionRef.get();

    const noticeFilesCollectionRef = this.ctx.userNoticeFilesRef(this.ref);
    const noticeFilesCollectionSnap = await noticeFilesCollectionRef.get();

    if (noticeFilesCollectionSnap.docs) {
      for (const noticeFileSnap of noticeFilesCollectionSnap.docs) {
        await noticeFileSnap.ref.delete();
      }
    }

    if (draftFilesCollectionSnap.docs) {
      for (const draftFileSnap of draftFilesCollectionSnap.docs) {
        await noticeFilesCollectionRef.add(draftFileSnap.data());
        getErrorReporter().logInfo(
          'Added draft file to notice files. Deleting draft...',
          {
            draftId: draftFileSnap.id
          }
        );
        await draftFileSnap.ref.delete();
      }
    }
  }

  /**
   * Adds the draft mails from a notice draft to a notice by copying them
   * and deleting them from the draft.
   *
   * @param {ERef<ENoticeDraft>} draftRef - The reference to the notice draft.
   * @return {Promise<void>} - A promise that resolves when the operation is complete.
   */
  public async addDraftMailToNotice(
    draftRef: ERef<ENoticeDraft>
  ): Promise<void> {
    const draftMailCollectionRef = draftRef.collection(Collections.mail);
    const draftMailCollectionSnap = await draftMailCollectionRef.get();

    const noticeMailCollectionRef = this.ref.collection(Collections.mail);
    const noticeMailCollectionSnap = await noticeMailCollectionRef.get();

    if (noticeMailCollectionSnap.docs) {
      for (const doc of noticeMailCollectionSnap.docs) {
        const mRef = doc.ref;
        await mRef.delete();
      }
    }

    if (draftMailCollectionSnap.docs) {
      for (const doc of draftMailCollectionSnap.docs) {
        await noticeMailCollectionRef.add(doc.data());
        const mRef = doc.ref;
        await mRef.delete();
      }
    }
  }

  /**
   * Clean properties associated with a draft notice in preparation of publication
   *
   * @param {ENoticeDraft} noticeObject - The draft notice object to be cleaned.
   * @returns {Promise<ENoticeDraft>} - The cleaned draft notice object.
   */
  private async cleanDraftNoticeProperties(
    noticeObject: ENoticeDraft
  ): Promise<ENoticeDraft> {
    const UpdatedNoticeObject = noticeObject;
    delete (UpdatedNoticeObject as any).anonymousFilerId;
    delete (UpdatedNoticeObject as any).inactive;
    delete (UpdatedNoticeObject as any).original;
    delete (UpdatedNoticeObject as any).confirming;
    delete (UpdatedNoticeObject as any).unusedDisplay;
    delete (UpdatedNoticeObject as any).generatedAffidavitURL;
    delete (UpdatedNoticeObject as any).generatedAffidavitStoragePath;
    delete (UpdatedNoticeObject as any).invoice;
    if (UpdatedNoticeObject.displayParams) {
      delete UpdatedNoticeObject.displayParams.imgs;
      delete (UpdatedNoticeObject as any).displayParams.err;
    }
    return UpdatedNoticeObject;
  }

  /**
   * Clears affidavit url and path on notice which forces regeneration.
   *
   * @return {Promise<void>} - A promise that resolves when the affidavit is regenerated successfully.
   */
  public async clearAffidavitOnNotice(): Promise<void> {
    await this.update({
      generatedAffidavitURL: '',
      generatedAffidavitStoragePath: ''
    });
  }

  public async updateAutomatedAffidavitConfiguration(
    affidavitReconciliationSettings: Partial<AffidavitReconciliationSettings>
  ): Promise<void> {
    const newAffidavitReconciliationSettings = removeUndefinedFields({
      ...this.modelData.affidavitReconciliationSettings,
      ...affidavitReconciliationSettings
    });

    const publisher = await this.getPublisher();
    const { response: automatedAffidavitConfiguration, error } =
      await getNoticeAAC(
        this.ctx,
        publisher,
        newAffidavitReconciliationSettings
      );

    if (error) {
      getErrorReporter().logAndCaptureError(
        ColumnService.AFFIDAVITS,
        error,
        'Failed to get automated affidavit configuration from affidavit reconciliation settings for notice',
        { noticeId: this.id }
      );

      return await this.update({
        affidavitReconciliationSettings: newAffidavitReconciliationSettings
      });
    }

    await this.update(
      removeUndefinedFields({
        affidavitReconciliationSettings: newAffidavitReconciliationSettings,
        automatedAffidavitConfiguration
      })
    );
  }

  public async getDisplayParamVariants(
    indesignClient: IndesignServerClient,
    domParser: typeof DOMParser,
    options?: {
      disableVariants?: boolean;
    }
  ) {
    const { displayParams, displayParamVariants } = this.modelData;
    if (!displayParams) {
      return wrapError(
        new Error('No display params found, cannot get variants')
      );
    }
    // if notice type is a display ad, we just return the original single display params.
    // we cannot optimize display ads into multiple variants because they are pre-designed with fixed dimensions.
    if (this.modelData.noticeType === NoticeType.display_ad.value) {
      await this.update({
        displayParamVariants: [
          {
            baseDisplayParamsHash: hash(displayParams),
            ...displayParams
          }
        ]
      });
      return wrapSuccess([displayParams]);
    }
    const template = this.modelData.adTemplate;
    if (!template) {
      return wrapError(new Error('No ad template found, cannot get variants'));
    }
    const [adTemplateError, adTemplate] = await safeGetOrThrow(template);
    if (adTemplateError || !adTemplate) {
      return wrapError(
        adTemplateError ||
          new Error('No ad template found, cannot get variants')
      );
    }

    const displayParamHash = hash(displayParams);
    const existingVariants = (displayParamVariants || []).filter(
      variant => variant.baseDisplayParamsHash === displayParamHash
    );

    if (existingVariants.length > 0) {
      return wrapSuccess(existingVariants);
    }

    const newDisplayParamVariants: Required<ENotice>['displayParamVariants'] =
      [];

    const oneColDisplayData = await requestDisplayParameters(
      this.ctx,
      indesignClient,
      this,
      domParser,
      {
        optimizeColumns: false,
        resizeTextFramesForProofPDF: false
      },
      {
        columns: this.modelData.columns,
        textColumnCount: 1,
        quality: 'high'
      }
    );
    delete oneColDisplayData.imgs;
    newDisplayParamVariants.push({
      baseDisplayParamsHash: displayParamHash,
      ...oneColDisplayData
    });

    if (options?.disableVariants) {
      await this.update({
        displayParamVariants: newDisplayParamVariants
      });
      return wrapSuccess(newDisplayParamVariants);
    }

    const twoColDisplayData = await requestDisplayParameters(
      this.ctx,
      indesignClient,
      this,
      domParser,
      {
        optimizeColumns: false,
        resizeTextFramesForProofPDF: false
      },
      {
        columns: this.modelData.columns * 2,
        textColumnCount: 2,
        quality: 'high'
      }
    );
    delete twoColDisplayData.imgs;
    newDisplayParamVariants.push({
      baseDisplayParamsHash: displayParamHash,
      ...twoColDisplayData
    });

    // If the original notice is 3 columns, generate a 2 column variant too for easier alignment with other notices that have even column counts.
    if (this.modelData.columns === 3) {
      const fixedTwoColVariant = await requestDisplayParameters(
        this.ctx,
        indesignClient,
        this,
        domParser,
        {
          optimizeColumns: false,
          resizeTextFramesForProofPDF: false
        },
        {
          columns: 2,
          textColumnCount: 2,
          quality: 'high'
        }
      );
      delete fixedTwoColVariant.imgs;
      newDisplayParamVariants.push({
        baseDisplayParamsHash: displayParamHash,
        ...fixedTwoColVariant
      });
    }

    if (
      twoColDisplayData.height *
        (adTemplate.data().styles?.pointsPerInch || 1) >
      (adTemplate.data().styles?.pageHeight ?? 0)
    ) {
      const threeColVariant = await requestDisplayParameters(
        this.ctx,
        indesignClient,
        this,
        domParser,
        {
          optimizeColumns: false,
          resizeTextFramesForProofPDF: false
        },
        {
          columns: this.modelData.columns * 3,
          textColumnCount: 3,
          quality: 'high'
        }
      );
      delete threeColVariant.imgs;
      newDisplayParamVariants.push({
        baseDisplayParamsHash: displayParamHash,
        ...threeColVariant
      });

      if (
        threeColVariant.height *
          (adTemplate.data().styles?.pointsPerInch || 1) >
        (adTemplate.data().styles?.pageHeight ?? 0)
      ) {
        const fourColVariant = await requestDisplayParameters(
          this.ctx,
          indesignClient,
          this,
          domParser,
          {
            optimizeColumns: false,
            resizeTextFramesForProofPDF: false
          },
          {
            columns: this.modelData.columns * 4,
            textColumnCount: 4,
            quality: 'high'
          }
        );
        delete fourColVariant.imgs;
        newDisplayParamVariants.push({
          baseDisplayParamsHash: displayParamHash,
          ...fourColVariant
        });
      }
    }

    await this.update({
      displayParamVariants: newDisplayParamVariants
    });

    return wrapSuccess(newDisplayParamVariants);
  }

  public async updateAdTemplate(
    adTemplateRef: ERef<ETemplate>
  ): Promise<ResponseOrError<void>> {
    const [error] = await safeAsync(() =>
      this.update({
        adTemplate: adTemplateRef
      })
    )();
    if (error) {
      return wrapError(error);
    }
    return wrapSuccess(undefined);
  }
}
